import { useState, useEffect } from 'react';
import { RefreshCw, ExternalLink, Megaphone, TrendingUp, Users, Database, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// LP slug → human label (kept in sync with /app/frontend/src/pages/lp/lpConfig.js)
const SLUG_LABEL = {
  'tax-planning': 'Tax Planning & Prep',
  'business-tax': 'Business Tax Planning',
  'accounting-bookkeeping': 'Accounting & Bookkeeping',
  'small-business-cpa': 'Small Business CPA',
  'irs-tax-relief': 'IRS Tax Relief',
  'irs-notice-help': 'IRS Notice Help',
};

const PpcAdsTab = ({ sessionToken }) => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Phase 3 — UTM/Campaign filter (clears P1 backlog item)
  const [utmSourceFilter, setUtmSourceFilter] = useState('');
  const [utmCampaignFilter, setUtmCampaignFilter] = useState('');
  const [exporting, setExporting] = useState(false);

  // Stream the CSV to the browser as a blob, then trigger a download.
  // Respects the currently selected UTM filters so what you see is what
  // you get. Auth header is required so we can't use a plain <a> link.
  const handleExportCsv = async () => {
    if (!sessionToken || exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (utmSourceFilter) params.set('utm_source', utmSourceFilter);
      if (utmCampaignFilter) params.set('utm_campaign', utmCampaignFilter);
      const qs = params.toString();
      const url = `${BACKEND_URL}/api/v1/admin/ppc-ads/export.csv${qs ? `?${qs}` : ''}`;
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (!r.ok) {
        const text = await r.text().catch(() => '');
        throw new Error(text || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const count = r.headers.get('x-record-count') || '?';
      const fileUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = fileUrl;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `ppc_ads_leads_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(fileUrl), 1500);
      toast.success(`Exported ${count} PPC leads`);
    } catch (e) {
      console.error(e);
      toast.error(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  const fetchAll = async () => {
    if (!sessionToken) return;
    setRefreshing(true);
    try {
      const [statsRes, leadsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/v1/admin/ppc-ads/stats`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        }),
        fetch(`${BACKEND_URL}/api/v1/admin/leads?lead_type=ppc_ads&limit=50`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        }),
      ]);

      if (statsRes.ok) {
        const sd = await statsRes.json();
        setStats(sd);
      } else {
        toast.error('Failed to load PPC stats');
      }
      if (leadsRes.ok) {
        const ld = await leadsRes.json();
        setLeads(ld.leads || []);
      }
    } catch (e) {
      console.error(e);
      toast.error('Error loading PPC data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!sessionToken) return;
    let cancelled = false;
    (async () => {
      setRefreshing(true);
      try {
        const [statsRes, leadsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/v1/admin/ppc-ads/stats`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
          }),
          fetch(`${BACKEND_URL}/api/v1/admin/leads?lead_type=ppc_ads&limit=50`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
          }),
        ]);
        if (cancelled) return;
        if (statsRes.ok) setStats(await statsRes.json());
        if (leadsRes.ok) {
          const ld = await leadsRes.json();
          setLeads(ld.leads || []);
        }
      } catch (e) {
        if (!cancelled) console.error(e);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionToken]);

  if (loading) {
    return (
      <div className="p-6 text-gray-300" data-testid="ppc-ads-tab-loading">
        Loading PPC Ads data…
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="ppc-ads-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-ivory flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#D4AF37]" />
            PPC Ads Landing Pages
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Leads from <code className="text-[#D4AF37]">/lp/*</code> Google Ads landing pages. Dual-write to main DB + bridge DB (app.kgob.com consumes the bridge).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={fetchAll}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 bg-navy-light hover:bg-navy text-ivory text-sm rounded-md disabled:opacity-50"
          data-testid="ppc-ads-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <button
          onClick={handleExportCsv}
          disabled={exporting}
          className="flex items-center gap-2 px-3 py-2 bg-[#D4AF37] hover:bg-[#b89530] text-navy text-sm font-semibold rounded-md disabled:opacity-50"
          data-testid="ppc-ads-export-csv"
          title={utmSourceFilter || utmCampaignFilter ? 'Export respects current UTM filters' : 'Export all PPC leads'}
        >
          <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
        </div>
      </div>

      {/* Top-line metrics */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Total leads" value={stats.total} icon={Users} />
          <MetricCard label="Last 24 hours" value={stats.last_24h} icon={TrendingUp} />
          <MetricCard label="Last 7 days" value={stats.last_7d} icon={TrendingUp} />
          <MetricCard label="Last 30 days" value={stats.last_30d} icon={TrendingUp} />
        </div>
      )}

      {/* Per-LP breakdown */}
      {stats && (
        <div className="bg-navy-light/40 border border-gray-700 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ivory">Leads by landing page</h3>
            <span className="text-xs text-gray-400">Click slug to open the live LP</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ppc-ads-by-slug-table">
              <thead className="bg-navy/50 text-gray-400 uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-2">Landing page</th>
                  <th className="text-left px-4 py-2">Slug</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-right px-4 py-2">Last 30d</th>
                  <th className="text-center px-4 py-2">View</th>
                </tr>
              </thead>
              <tbody className="text-ivory">
                {(stats.by_slug || []).map((row) => (
                  <tr key={row.slug} className="border-t border-gray-800 hover:bg-navy/30">
                    <td className="px-4 py-2">{SLUG_LABEL[row.slug] || row.slug}</td>
                    <td className="px-4 py-2 text-gray-400">
                      <code>{row.slug}</code>
                    </td>
                    <td className="px-4 py-2 text-right font-semibold">{row.total}</td>
                    <td className="px-4 py-2 text-right">{row.last_30d}</td>
                    <td className="px-4 py-2 text-center">
                      <a data-testid="ppc-ads-tab-a-1"
                        href={`/lp/${row.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Open
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Client type + bridge status */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-navy-light/40 border border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-ivory mb-3">Client type split</h3>
            <div className="flex items-center gap-4">
              <div className="flex-1 bg-navy/50 rounded px-3 py-2">
                <div className="text-xs text-gray-400">Individuals</div>
                <div className="text-xl font-semibold text-ivory">{stats.by_client_type.individual}</div>
              </div>
              <div className="flex-1 bg-navy/50 rounded px-3 py-2">
                <div className="text-xs text-gray-400">Businesses</div>
                <div className="text-xl font-semibold text-ivory">{stats.by_client_type.business}</div>
              </div>
            </div>
          </div>
          <div className="bg-navy-light/40 border border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-ivory mb-3 flex items-center gap-2">
              <Database className="w-4 h-4 text-[#D4AF37]" />
              CRM Bridge (app.kgob.com)
            </h3>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${stats.bridge_status.connected ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-gray-300">
                  {stats.bridge_status.connected ? 'Bridge connected' : 'Bridge disconnected'}
                </span>
              </div>
              <div className="text-gray-300">
                Total in bridge: <span className="font-semibold text-ivory">{stats.bridge_status.total_in_bridge}</span>
              </div>
              <div className="text-gray-300">
                Not yet pulled by app.kgob.com:{' '}
                <span className={`font-semibold ${stats.bridge_status.not_pulled_by_app_kgob > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                  {stats.bridge_status.not_pulled_by_app_kgob}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent leads list */}
      <div className="bg-navy-light/40 border border-gray-700 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-ivory">Recent PPC leads (last 50)</h3>
          {/* UTM filters (Phase 3 — clears P1 backlog) */}
          <div className="flex items-center gap-2" data-testid="ppc-utm-filters">
            <select
              data-testid="ppc-utm-source-filter"
              value={utmSourceFilter}
              onChange={(ev) => setUtmSourceFilter(ev.target.value)}
              className="text-xs bg-navy-light border border-gray-700 rounded px-2 py-1 text-ivory"
            >
              <option value="">All UTM sources</option>
              {Array.from(new Set(leads.map((l) => l.lp_meta?.utm_source).filter(Boolean))).sort().map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              data-testid="ppc-utm-campaign-filter"
              value={utmCampaignFilter}
              onChange={(ev) => setUtmCampaignFilter(ev.target.value)}
              className="text-xs bg-navy-light border border-gray-700 rounded px-2 py-1 text-ivory"
            >
              <option value="">All campaigns</option>
              {Array.from(new Set(leads.map((l) => l.lp_meta?.utm_campaign).filter(Boolean))).sort().map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {(utmSourceFilter || utmCampaignFilter) && (
              <button
                onClick={() => { setUtmSourceFilter(''); setUtmCampaignFilter(''); }}
                className="text-xs text-[#11a0c2] hover:underline"
                data-testid="ppc-utm-clear"
              >Clear</button>
            )}
          </div>
        </div>
        {(() => {
          // Filter leads by UTM source + campaign before rendering
          const filteredLeads = leads.filter((l) => {
            if (utmSourceFilter && l.lp_meta?.utm_source !== utmSourceFilter) return false;
            if (utmCampaignFilter && l.lp_meta?.utm_campaign !== utmCampaignFilter) return false;
            return true;
          });
          return filteredLeads.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              {leads.length === 0
                ? 'No PPC leads yet. Submissions to /lp/* will appear here.'
                : 'No leads match those UTM filters.'}
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ppc-ads-leads-table">
              <thead className="bg-navy/50 text-gray-400 uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-2">When</th>
                  <th className="text-left px-4 py-2">LP</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Business</th>
                  <th className="text-left px-4 py-2">Contact</th>
                  <th className="text-left px-4 py-2">Source</th>
                </tr>
              </thead>
              <tbody className="text-ivory">
                {(filteredLeads || []).map((lead) => (
                  <tr key={lead._id || lead.lead_id} className="border-t border-gray-800 hover:bg-navy/30" data-testid={`ppc-lead-row-${lead._id}`}>
                    <td className="px-4 py-2 text-gray-400 whitespace-nowrap">
                      {lead.created_at ? new Date(lead.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-300 whitespace-nowrap">
                      <code className="text-xs">{lead.lp_slug || '—'}</code>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${lead.client_type === 'business' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'}`}>
                        {lead.client_type || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2">{lead.first_name} {lead.last_name}</td>
                    <td className="px-4 py-2 text-gray-300">{lead.business_name || '—'}</td>
                    <td className="px-4 py-2 text-gray-300">
                      <div className="text-xs">{lead.email}</div>
                      <div className="text-xs text-gray-500">{lead.phone}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-400 text-xs">
                      {lead.lp_meta?.utm_source && (
                        <div>utm: {lead.lp_meta.utm_source}/{lead.lp_meta.utm_campaign || '—'}</div>
                      )}
                      {lead.lp_meta?.gclid && (
                        <div className="text-[#D4AF37]">gclid ✓</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        })()}
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, icon: Icon }) => (
  <div className="bg-navy-light/40 border border-gray-700 rounded-lg px-4 py-3">
    <div className="flex items-center justify-between mb-1">
      <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      {Icon && <Icon className="w-4 h-4 text-[#D4AF37]" />}
    </div>
    <div className="text-2xl font-semibold text-ivory">{value}</div>
  </div>
);

export default PpcAdsTab;
